"use strict";
// Signatures are compared byte-for-byte, so any incidental per-instance or
// per-build difference in a raw value — a mangled class name that changes
// across compiler runs, a UUID or row index baked into one list row's resource
// id, a namespace prefix that varies by package — would fingerprint two
// genuinely identical elements as distinct and defeat Type I dedup entirely.
// Reducing those fields to their stable part is what makes exact-match
// comparison work at all.
//
// Every step must be idempotent: normalize(normalize(x)) === normalize(x).
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildFieldNormalizer = exports.FieldNormalizer = exports.STRIP_ANDROID_RES_PREFIX = exports.ROW_INDEX_STRIP = exports.UUID_STRIP = exports.NAMESPACE_PREFIX_STRIP_ROLE = exports.NAMESPACE_PREFIX_STRIP_CLASSNAME = exports.KOTLIN_SYNTHETIC_STRIP = exports.SWIFT_DEMANGLE = exports.androidResPrefixStrip = exports.rowIndexStrip = exports.uuidStrip = exports.namespacePrefixStrip = exports.kotlinSyntheticStrip = exports.swiftDemangle = void 0;
// Swift ABI mangling prefixes we attempt to decode. Reference:
//   https://github.com/apple/swift/blob/main/docs/ABI/Mangling.rst
// The body is a sequence of <length><identifier> segments. We parse them
// greedily; if the lengths don't consume the body cleanly we fall through
// unchanged rather than risk producing garbage.
// `t[CVOPF]+` (quantified) rather than `t[CVOPF]` lets this alternative
// consume multi-level nested-class context markers (`_TtCC...`, one `[CVOPF]`
// letter per nesting level) instead of matching only the first letter and
// leaving the rest of the marker to abort the length-prefixed parse below.
const SWIFT_PREFIX_RE = /^_T(t[CVOPF]+|tG[CVOPF]+|tG?C[CVOPF]*)/;
const SWIFT_LENGTH_RE = /^(\d+)/;
// Swift's private-declaration discriminator: `P<digits>_` followed by a
// 32-hex-char hash (e.g. `P33_6C3DC49E2BB0ACF6501CE64D30D54BC3`). This
// segment identifies the private lexical context a symbol was declared in
// and is not itself a length-prefixed identifier — left unhandled it aborts
// the parse loop below (the hash starts with a non-digit after the first
// character). Detected and skipped whole so the length-prefixed parse can
// resume with the identifier segment that follows it.
const SWIFT_PRIVATE_DISCRIMINATOR_RE = /^P\d+_[0-9A-Fa-f]{32}/;
const swiftDemangle = (value) => {
    const m = SWIFT_PREFIX_RE.exec(value);
    if (!m) {
        return value;
    }
    let body = value.slice(m[0].length);
    const parts = [];
    while (body.length > 0) {
        const discriminatorMatch = SWIFT_PRIVATE_DISCRIMINATOR_RE.exec(body);
        if (discriminatorMatch) {
            body = body.slice(discriminatorMatch[0].length);
            continue;
        }
        const lengthMatch = SWIFT_LENGTH_RE.exec(body);
        if (!lengthMatch) {
            // Generic substitutions, runtime tokens and the like: refuse to
            // demangle rather than emit a half-parsed name.
            return value;
        }
        const n = parseInt(lengthMatch[1], 10);
        body = body.slice(lengthMatch[0].length);
        if (body.length < n) {
            return value;
        }
        parts.push(body.slice(0, n));
        body = body.slice(n);
    }
    if (parts.length === 0) {
        return value;
    }
    return parts[parts.length - 1];
};
exports.swiftDemangle = swiftDemangle;
/**
 * Strips a trailing run of `<delimiter><digits>` groups.
 *
 * Scans backwards instead of using `/(?:<delim>\d+)+$/`. That pattern nests a
 * quantifier inside a quantifier, so on a value whose tail is digits but whose
 * head does not match, the engine tries every partition of the digit run before
 * failing — quadratic in the value's length. `viewIdResourceName` and
 * `accessibilityIdentifier` reach here straight off the request body with no
 * length bound, so a 100 KB value blocked the event loop for seconds. The
 * backward scan visits each character once.
 */
const stripTrailingDelimitedDigits = (value, delimiter) => {
    let end = value.length;
    let i = value.length;
    while (i > 0) {
        let j = i;
        while (j > 0 &&
            value.charCodeAt(j - 1) >= 48 &&
            value.charCodeAt(j - 1) <= 57) {
            j--;
        }
        if (j === i || j === 0 || value[j - 1] !== delimiter) {
            break;
        }
        i = j - 1;
        end = i;
    }
    return end === value.length ? value : value.slice(0, end);
};
// Kotlin/Java anonymous-inner-class suffixes: $1, $1$1, $Lambda$3, etc.
// We strip ONLY trailing $\d+ chains so meaningful nested classes
// (`Outer$Inner`) survive.
const kotlinSyntheticStrip = (value) => stripTrailingDelimitedDigits(value, '$');
exports.kotlinSyntheticStrip = kotlinSyntheticStrip;
const namespacePrefixStrip = (value) => {
    if (!value.includes('.')) {
        return value;
    }
    return value.slice(value.lastIndexOf('.') + 1);
};
exports.namespacePrefixStrip = namespacePrefixStrip;
// Anchored canonical UUID. The `[\s_:-]?` prefix consumes an optional
// adjacent separator (`-`, `_`, `:`, whitespace) so `foo-<uuid>` collapses
// cleanly to `foo` rather than leaving a trailing `-`.
const UUID_RE = /[\s_:-]?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const uuidStrip = (value) => value.replace(UUID_RE, '');
exports.uuidStrip = uuidStrip;
// Strips the whole `-<digits>` chain rather than its last link: stripping one
// link per pass would leave the step non-idempotent (`Cell-1-2` -> `Cell-1`).
const rowIndexStrip = (value) => stripTrailingDelimitedDigits(value, '-');
exports.rowIndexStrip = rowIndexStrip;
// Android resource-ID prefix: `<package_name>:<resource_type>/<entry_name>`
// per AAPT. `<package_name>` is alphanumeric with dots/underscores; the type
// segment is restricted to AAPT's documented vocabulary so a stray
// `foo:bar/baz` value can't trigger a strip.
const ANDROID_RES_PREFIX_RE = /^[A-Za-z][A-Za-z0-9_.]*:(?:id|string|drawable|layout|color|dimen|style|attr|raw|menu|anim|animator|integer|bool|array|font|interpolator|mipmap|navigation|transition|xml)\//g;
const androidResPrefixStrip = (value) => value.replace(ANDROID_RES_PREFIX_RE, '');
exports.androidResPrefixStrip = androidResPrefixStrip;
exports.SWIFT_DEMANGLE = {
    name: 'swift_demangle',
    fields: new Set(['className']),
    transform: exports.swiftDemangle
};
exports.KOTLIN_SYNTHETIC_STRIP = {
    name: 'kotlin_synthetic_strip',
    fields: new Set(['className']),
    transform: exports.kotlinSyntheticStrip
};
exports.NAMESPACE_PREFIX_STRIP_CLASSNAME = {
    name: 'namespace_prefix_strip',
    fields: new Set(['className']),
    transform: exports.namespacePrefixStrip
};
exports.NAMESPACE_PREFIX_STRIP_ROLE = {
    name: 'namespace_prefix_strip',
    fields: new Set(['role']),
    transform: exports.namespacePrefixStrip
};
exports.UUID_STRIP = {
    name: 'uuid_strip',
    fields: new Set(['id', 'res']),
    transform: exports.uuidStrip
};
exports.ROW_INDEX_STRIP = {
    name: 'row_index_strip',
    fields: new Set(['id', 'res']),
    transform: exports.rowIndexStrip
};
exports.STRIP_ANDROID_RES_PREFIX = {
    name: 'strip_android_resource_prefix',
    fields: new Set(['res']),
    transform: exports.androidResPrefixStrip
};
class FieldNormalizer {
    stepsInternal;
    constructor(steps = []) {
        this.stepsInternal = [...steps];
    }
    get steps() {
        return [...this.stepsInternal];
    }
    /**
     * Non-string values are returned unchanged. An exception from a step leaves
     * the in-flight value alone and continues to the next — a misbehaving rule
     * never destroys a field.
     */
    normalize(value, field) {
        if (typeof value !== 'string') {
            return value;
        }
        let out = value;
        for (const step of this.stepsInternal) {
            if (!step.fields.has(field)) {
                continue;
            }
            try {
                out = step.transform(out);
            }
            catch {
                continue;
            }
        }
        return out;
    }
}
exports.FieldNormalizer = FieldNormalizer;
const buildFieldNormalizer = () => {
    const steps = [
        exports.NAMESPACE_PREFIX_STRIP_CLASSNAME,
        exports.SWIFT_DEMANGLE,
        exports.KOTLIN_SYNTHETIC_STRIP,
        exports.NAMESPACE_PREFIX_STRIP_ROLE,
        exports.UUID_STRIP,
        exports.ROW_INDEX_STRIP,
        // Runs AFTER uuid/row-index so a value like `com.app:id/Cell-01-<uuid>`
        // collapses cleanly to `Cell`.
        exports.STRIP_ANDROID_RES_PREFIX
    ];
    return new FieldNormalizer(steps);
};
exports.buildFieldNormalizer = buildFieldNormalizer;
//# sourceMappingURL=normalizer.js.map